/** Pure helpers for the RFI Register (RFIManager) and its form (RFIForm), and
 *  their side of the header bridge. The register and the form own their data;
 *  they publish the state below so the global header can draw the title,
 *  search, status filter, New RFI and the form's Save / Issue / PDF pills, and
 *  those pills fire the events below back. Kept free of React so vitest covers
 *  the counting and filtering rules. */

import type { RfiRecord } from '../../types/rfis';

export type { RfiRecord };
export type RfiStatus = 'draft' | 'issued' | 'responded' | 'closed';
export type RfiStatusFilter = 'all' | RfiStatus | 'overdue';

export const RFI_STATUS_FILTERS: ReadonlyArray<{ id: RfiStatusFilter; label: string }> = [
  { id: 'all', label: 'All RFIs' },
  { id: 'draft', label: 'Draft' },
  { id: 'issued', label: 'Issued' },
  { id: 'responded', label: 'Responded' },
  { id: 'closed', label: 'Closed' },
  { id: 'overdue', label: 'Overdue' },
];

const STATUS_LABEL: Record<RfiStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  responded: 'Responded',
  closed: 'Closed',
};

export function rfiStatusLabel(status: string): string {
  if (status in STATUS_LABEL) return STATUS_LABEL[status as RfiStatus];
  const text = status.replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Unknown';
}

/** Today's date on this device as YYYY-MM-DD, the form's own date format. */
export function localDateKey(date: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** An issued RFI whose response date has passed. Due today is not overdue. */
export function isRfiOverdue(rfi: Pick<RfiRecord, 'status' | 'responseDeadline'>, todayKey: string): boolean {
  return rfi.status === 'issued' && !!rfi.responseDeadline && rfi.responseDeadline.slice(0, 10) < todayKey;
}

const DAY_MS = 86_400_000;

// Both keys become UTC midnights, so a clock change between them cannot add or
// lose an hour and tip the count.
function dayNumber(dateKey: string): number {
  const [year, month, day] = dateKey.slice(0, 10).split('-').map(Number);
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

/** Whole days past the response date of an overdue RFI, or null when it is not overdue. */
export function rfiDaysOverdue(rfi: Pick<RfiRecord, 'status' | 'responseDeadline'>, todayKey: string): number | null {
  if (!isRfiOverdue(rfi, todayKey) || !rfi.responseDeadline) return null;
  return Math.round(dayNumber(todayKey) - dayNumber(rfi.responseDeadline));
}

/** "3 days overdue" for an overdue RFI, or null when it is not overdue. */
export function rfiOverdueLabel(rfi: Pick<RfiRecord, 'status' | 'responseDeadline'>, todayKey: string): string | null {
  const days = rfiDaysOverdue(rfi, todayKey);
  if (days === null) return null;
  return days === 1 ? '1 day overdue' : `${days} days overdue`;
}

export function countRfis(rfis: RfiRecord[], todayKey: string): Record<RfiStatusFilter, number> {
  const counts: Record<RfiStatusFilter, number> = {
    all: rfis.length,
    draft: 0,
    issued: 0,
    responded: 0,
    closed: 0,
    overdue: 0,
  };
  for (const rfi of rfis) {
    if (rfi.status in STATUS_LABEL) counts[rfi.status as RfiStatus] += 1;
    if (isRfiOverdue(rfi, todayKey)) counts.overdue += 1;
  }
  return counts;
}

export function filterRfis(
  rfis: RfiRecord[],
  { query, status }: { query: string; status: RfiStatusFilter },
  todayKey: string,
): RfiRecord[] {
  const needle = query.trim().toLowerCase();
  return rfis.filter((rfi) => {
    if (status === 'overdue' ? !isRfiOverdue(rfi, todayKey) : status !== 'all' && rfi.status !== status) return false;
    if (!needle) return true;
    return [rfi.rfiNumber, rfi.subject, rfi.addressee, rfi.createdByName]
      .some((field) => (field ?? '').toLowerCase().includes(needle));
  });
}

/** "12 RFIs, 1 overdue", or null for an empty register. */
export function rfiRegisterSubtitle(counts: Record<RfiStatusFilter, number>): string | null {
  if (counts.all === 0) return null;
  const total = counts.all === 1 ? '1 RFI' : `${counts.all} RFIs`;
  return counts.overdue > 0 ? `${total}, ${counts.overdue} overdue` : total;
}

const DATE_LABEL = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });

/** "14 Aug 2026" for a YYYY-MM-DD date, or null when there is none. */
export function formatRfiDate(dateKey: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateKey ?? '');
  if (!match) return null;
  return DATE_LABEL.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)));
}

// ── Header bridge ────────────────────────────────────────────────────────────

export interface RfiRegisterHeaderState {
  counts: Record<RfiStatusFilter, number>;
  statusFilter: RfiStatusFilter;
  query: string;
  subtitle: string | null;
}

export interface RfiFormHeaderState {
  /** "New RFI", or the RFI's number. */
  title: string;
  /** The subject of an RFI being edited. */
  subtitle: string | null;
  saving: 'save' | 'issue' | null;
  /** "Save draft" while the status is draft, otherwise "Save". */
  saveLabel: string;
  /** Only a draft can be issued. */
  canIssue: boolean;
  /** Only a saved RFI has a PDF. */
  canExport: boolean;
}

export const RFI_REGISTER_STATE_EVENT = 'rfis:state-change';
export const RFI_REGISTER_REQUEST_STATE_EVENT = 'rfis:request-state';
/** detail: { query: string } */
export const RFI_SET_QUERY_EVENT = 'rfis:set-query';
/** detail: { filter: RfiStatusFilter } */
export const RFI_SET_FILTER_EVENT = 'rfis:set-filter';
export const RFI_CREATE_EVENT = 'rfis:create-new';

export const RFI_FORM_STATE_EVENT = 'rfi-form:state-change';
export const RFI_FORM_REQUEST_STATE_EVENT = 'rfi-form:request-state';
export const RFI_FORM_CLOSE_EVENT = 'rfi-form:close';
export const RFI_FORM_SAVE_EVENT = 'rfi-form:save';
export const RFI_FORM_ISSUE_EVENT = 'rfi-form:issue';
export const RFI_FORM_EXPORT_EVENT = 'rfi-form:export-pdf';
